// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/pages/application/BusinessInfoPage.tsx
//
// Phase 1 Wave 12 Task 99 — Vendor application step 1.
//  + design PNG `public-become-vendor-register`.
// Phase 7 Wave 18 Task 78 batch C6 (Amendment #1) — i18n rewrite.

import { useNavigate } from 'react-router-dom';
import {
  Button,
  Form,
  FormField,
  FormLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  VendorApplicationStepperLayout,
} from '@printway/ui';

import { useTranslation } from '../../lib/i18n/index.js';
import { useApplicationStore } from './ApplicationStore.js';

export function BusinessInfoPage(): JSX.Element {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const data = useApplicationStore((s) => s.data.businessInfo);
  const setBusinessInfo = useApplicationStore((s) => s.setBusinessInfo);

  const COUNTRIES = [
    { value: '', label: t('application.businessInfo.countrySelect') },
    { value: 'US', label: t('application.businessInfo.countryUS') },
    { value: 'VN', label: t('application.businessInfo.countryVN') },
    { value: 'GB', label: t('application.businessInfo.countryGB') },
    { value: 'CA', label: t('application.businessInfo.countryCA') },
    { value: 'AU', label: t('application.businessInfo.countryAU') },
    { value: 'OTHER', label: t('application.businessInfo.countryOther') },
  ];

  const canContinue =
    data.businessType !== '' &&
    data.fullName.trim().length > 0 &&
    data.email.trim().length > 0 &&
    data.street.trim().length > 0 &&
    data.city.trim().length > 0 &&
    data.country !== '';

  return (
    <VendorApplicationStepperLayout currentStep={1}>
      <h2 className="text-xl font-semibold text-pw-text">{t('application.businessInfo.heading')}</h2>
      <p className="mt-1 text-sm text-pw-text-muted">
        {t('application.businessInfo.description')}
      </p>

      <Form className="mt-6 space-y-4" onSubmit={(e) => e.preventDefault()}>
        <FormField>
          <FormLabel htmlFor="businessType">{t('application.businessInfo.businessType')}</FormLabel>
          <Select
            value={data.businessType === '' ? undefined : data.businessType}
            onValueChange={(value) =>
              setBusinessInfo({ businessType: value as 'individual' | 'business' })
            }
          >
            <SelectTrigger id="businessType">
              <SelectValue placeholder={t('application.businessInfo.businessTypePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="individual">{t('application.businessInfo.typeIndividual')}</SelectItem>
              <SelectItem value="business">{t('application.businessInfo.typeBusiness')}</SelectItem>
            </SelectContent>
          </Select>
        </FormField>

        <FormField>
          <FormLabel htmlFor="fullName">{t('application.businessInfo.fullName')}</FormLabel>
          <Input
            id="fullName"
            value={data.fullName}
            onChange={(e) => setBusinessInfo({ fullName: e.target.value })}
            placeholder={t('application.businessInfo.fullNamePlaceholder')}
            autoComplete="name"
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField>
            <FormLabel htmlFor="email">{t('application.businessInfo.email')}</FormLabel>
            <Input
              id="email"
              type="email"
              value={data.email}
              onChange={(e) => setBusinessInfo({ email: e.target.value })}
              placeholder={t('application.businessInfo.emailPlaceholder')}
              autoComplete="email"
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor="phone">{t('application.businessInfo.phone')}</FormLabel>
            <Input
              id="phone"
              type="tel"
              value={data.phone}
              onChange={(e) => setBusinessInfo({ phone: e.target.value })}
              placeholder={t('application.businessInfo.phonePlaceholder')}
              autoComplete="tel"
            />
          </FormField>
        </div>

        <div className="border-t border-pw-border pt-4">
          <h3 className="text-sm font-semibold text-pw-text">{t('application.businessInfo.addressHeading')}</h3>
        </div>

        <FormField>
          <FormLabel htmlFor="street">{t('application.businessInfo.street')}</FormLabel>
          <Input
            id="street"
            value={data.street}
            onChange={(e) => setBusinessInfo({ street: e.target.value })}
            placeholder={t('application.businessInfo.streetPlaceholder')}
            autoComplete="street-address"
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField>
            <FormLabel htmlFor="city">{t('application.businessInfo.city')}</FormLabel>
            <Input
              id="city"
              value={data.city}
              onChange={(e) => setBusinessInfo({ city: e.target.value })}
              autoComplete="address-level2"
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor="state">{t('application.businessInfo.stateProvince')}</FormLabel>
            <Input
              id="state"
              value={data.state}
              onChange={(e) => setBusinessInfo({ state: e.target.value })}
              autoComplete="address-level1"
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField>
            <FormLabel htmlFor="postalCode">{t('application.businessInfo.postalCode')}</FormLabel>
            <Input
              id="postalCode"
              value={data.postalCode}
              onChange={(e) => setBusinessInfo({ postalCode: e.target.value })}
              autoComplete="postal-code"
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor="country">{t('application.businessInfo.country')}</FormLabel>
            <Select
              value={data.country === '' ? undefined : data.country}
              onValueChange={(value) => setBusinessInfo({ country: value })}
            >
              <SelectTrigger id="country">
                <SelectValue placeholder={t('application.businessInfo.countrySelect')} />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.filter((c) => c.value !== '').map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>

        <div className="flex items-center justify-end pt-4">
          <Button
            type="button"
            disabled={!canContinue}
            onClick={() => navigate('/application/store-setup')}
          >
            {t('application.businessInfo.continue')}
          </Button>
        </div>
      </Form>
    </VendorApplicationStepperLayout>
  );
}

export default BusinessInfoPage;
